import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { profileService } from "@/services/profileService";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Check, 
  Sparkles, 
  Mail, 
  Loader2, 
  ArrowRight,
  ShieldCheck,
  Award,
  Globe
} from "lucide-react";

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();

  const [monthlyEmail, setMonthlyEmail] = useState("");
  const [annualEmail, setAnnualEmail] = useState("");
  const [registeredPlan, setRegisteredPlan] = useState<"monthly" | "annual" | null>(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const handleJoinWaitlist = async (plan: "monthly" | "annual", e: React.FormEvent) => {
    e.preventDefault();
    const finalEmail = user?.email || (plan === "monthly" ? monthlyEmail : annualEmail);
    if (!finalEmail) return;

    setWaitlistLoading(true);
    try {
      await profileService.addToWaitlist(user?.id || "", finalEmail, "pricing", plan);
      setRegisteredPlan(plan);
      setMonthlyEmail("");
      setAnnualEmail("");
    } catch (err) {
      console.error("[Pricing] Failed to register waitlist interest:", err);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/pricing");
    } catch (err) {
      console.error("[Pricing] Sign out failed:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-primary/10 border-t-primary" />
      </div>
    );
  }

  const premiumFeatures = [
    t("pricing.feature_unlimited_tests", "Unlimited mock tests"),
    t("pricing.feature_unlimited_corrections", "Unlimited writing corrections"),
    t("pricing.feature_full_error_bank", "Full error bank"),
    t("pricing.feature_full_flashcards", "Full flashcards access"),
    t("pricing.feature_ai_coach", "AI TEF Coach"),
    t("pricing.feature_personalized_plan", "Personalized study plan"),
    t("pricing.feature_performance_analytics", "Performance analytics")
  ];

  return (
    <div className="min-h-screen w-full bg-[#F8F9FA] text-slate-800 flex flex-col font-sans overflow-x-hidden antialiased">
      {/* Unified Public Header */}
      <header className="w-full bg-white/80 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-50 shadow-2xs select-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-white font-extrabold text-lg shadow-sm">
                T
              </div>
              <span className="font-black text-slate-900 tracking-tight text-base">
                TEF Canada <span className="text-primary font-bold">Trainer</span>
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-5 text-xs font-bold text-slate-600">
              <Link to="/" className="hover:text-primary transition-colors">{t("common.home", "Home")}</Link>
              <Link to="/pricing" className="hover:text-primary transition-colors">{t("pricing.title", "Pricing")}</Link>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-28 sm:w-32">
              <LanguageSwitcher />
            </div>
            
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/dashboard">
                  <Button size="sm" className="bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs shadow-xs px-4 py-2">
                    {t("onboarding.go_to_dashboard", "Go to Dashboard")}
                  </Button>
                </Link>
                <Button size="sm" variant="ghost" onClick={handleLogout} className="text-slate-500 hover:text-slate-800 font-bold text-xs">
                  {t("settings.logout", "Sair")}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 font-bold text-xs px-3">
                    {t("settings.login", "Entrar")}
                  </Button>
                </Link>
                <Link to="/auth?mode=signup" className="hidden sm:inline-block">
                  <Button size="sm" className="bg-primary hover:bg-primary/95 text-white font-black rounded-xl text-xs shadow-xs px-4 py-2">
                    {t("landing.btn_start_free_trial", "Start Free Trial")}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Pricing Header Banner */}
      <section className="relative pt-16 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <Badge className="bg-primary/10 text-primary border-none font-extrabold px-3 py-1 text-[10px] uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          Flexible Preparation
        </Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-tight">
          {t("pricing.title", "Simple, transparent pricing")}
        </h1>
        <p className="text-sm text-slate-500 font-medium max-w-lg mx-auto">
          {t("pricing.subtitle", "Choose the plan that fits your study needs")}
        </p>
      </section>

      {/* Plans Comparison Grid */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto w-full">
        <div className="grid gap-8 md:grid-cols-3 items-stretch">
          
          {/* Free Trial Plan Card */}
          <Card className="border border-slate-200 shadow-sm bg-white p-6 rounded-3xl flex flex-col justify-between hover:border-slate-300 transition-all duration-300">
            <div className="space-y-5 text-left">
              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  {t("pricing.free_trial", "Free Trial")}
                </span>
                <CardTitle className="text-3xl font-black text-slate-900">
                  {t("pricing.free_trial_price", "Free")}
                </CardTitle>
                <CardDescription className="text-xs font-semibold text-slate-400 leading-normal">
                  {t("pricing.free_trial_desc", "Basic training to discover the platform format.")}
                </CardDescription>
              </div>

              <div className="border-t border-slate-100 pt-5 space-y-3.5 text-xs font-semibold text-slate-600">
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>4 mock tests (CE/CO)</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>3 writing corrections (EE)</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Flashcards preview (up to 50)</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Error bank preview (up to 50)</span>
                </div>
              </div>
            </div>

            <div className="pt-8">
              {user ? (
                <Button
                  variant="outline"
                  disabled
                  className="w-full h-12 border-slate-200 text-slate-400 font-bold rounded-2xl text-xs bg-slate-50 cursor-not-allowed"
                >
                  {t("pricing.btn_active_plan", "Active Plan")}
                </Button>
              ) : (
                <Link to="/auth?mode=signup" className="w-full">
                  <Button className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-2xl text-xs shadow-xs">
                    {t("pricing.free_trial", "Start Free Trial")}
                  </Button>
                </Link>
              )}
            </div>
          </Card>

          {/* Premium Monthly Card */}
          <Card className="border-none shadow-xl bg-gradient-to-br from-violet-600 via-indigo-600 to-indigo-700 text-white p-6 rounded-3xl flex flex-col justify-between relative overflow-hidden">
            <div className="absolute right-0 top-0 bg-amber-400 text-slate-900 font-black text-[9px] uppercase px-3.5 py-1.5 rounded-bl-2xl tracking-wider shadow-sm flex items-center gap-1 select-none">
              <Sparkles className="w-3 h-3 fill-slate-900" />
              <span>{t("upgrade.premiumRibbon", "Recomendado")}</span>
            </div>

            <div className="space-y-5 text-left">
              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase text-purple-200 tracking-wider">
                  {t("pricing.premium_monthly", "Premium Monthly")}
                </span>
                <CardTitle className="text-3xl font-black text-white flex items-baseline gap-1">
                  <span>Coming soon</span>
                </CardTitle>
                <CardDescription className="text-xs font-semibold text-purple-100 leading-normal">
                  {t("upgrade.premiumDesc", "Acesso ilimitado e sem barreiras a todas as funcionalidades.")}
                </CardDescription>
              </div>

              <div className="border-t border-indigo-500/50 pt-5 space-y-3.5 text-xs text-purple-50">
                {premiumFeatures.map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 font-bold">
                    <Check className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-8">
              {registeredPlan === "monthly" ? (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-center flex items-center justify-center gap-2 text-emerald-300 text-xs font-black">
                  <ShieldCheck className="w-4 h-4 shrink-0" />
                  <span>{t("pricing.waitlist_success", "You're on the waitlist.")}</span>
                </div>
              ) : (
                <form onSubmit={(e) => handleJoinWaitlist("monthly", e)} className="space-y-3">
                  {!user && (
                    <input
                      type="email"
                      required
                      placeholder={t("pricing.waitlist_email_placeholder", "Enter your email address")}
                      value={monthlyEmail}
                      onChange={(e) => setMonthlyEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-white/20 text-xs text-white placeholder-purple-200 focus:outline-hidden focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-white/10 font-bold text-center"
                    />
                  )}
                  {user && (
                    <div className="p-3 bg-white/10 border border-white/10 rounded-2xl text-xs font-bold text-purple-100 text-center">
                      {user.email}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={waitlistLoading}
                    className="w-full h-12 bg-amber-400 hover:bg-amber-350 text-slate-900 font-black rounded-2xl text-xs flex items-center justify-center gap-1.5 shadow-md border-none transition-all transform hover:scale-[1.01]"
                  >
                    {waitlistLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-slate-900" />
                    ) : (
                      <Mail className="w-4 h-4 text-slate-900" />
                    )}
                    <span>{t("pricing.btn_join_waitlist", "Join Waitlist")}</span>
                  </Button>
                </form>
              )}
            </div>
          </Card>

          {/* Premium Annual Card */}
          <Card className="border border-slate-200 shadow-sm bg-white p-6 rounded-3xl flex flex-col justify-between hover:border-slate-300 transition-all duration-300">
            <div className="space-y-5 text-left">
              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  {t("pricing.premium_annual", "Premium Annual")}
                </span>
                <CardTitle className="text-3xl font-black text-slate-900">
                  Coming soon
                </CardTitle>
                <CardDescription className="text-xs font-semibold text-slate-400 leading-normal">
                  Best value option with our launching waitlist discount included.
                </CardDescription>
              </div>

              <div className="border-t border-slate-100 pt-5 space-y-3.5 text-xs font-semibold text-slate-600">
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t("pricing.feature_everything_monthly", "Everything in Monthly")}</span>
                </div>
                <div className="flex items-center gap-2.5 text-primary">
                  <Sparkles className="w-4 h-4 text-primary shrink-0 animate-pulse" />
                  <span>{t("pricing.feature_best_value", "Best Value")}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t("pricing.feature_annual_discount", "Annual discount coming soon")}</span>
                </div>
              </div>
            </div>

            <div className="pt-8">
              {registeredPlan === "annual" ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl text-center flex items-center justify-center gap-2 text-emerald-800 text-xs font-black">
                  <ShieldCheck className="w-4 h-4 shrink-0" />
                  <span>{t("pricing.waitlist_success", "You're on the waitlist.")}</span>
                </div>
              ) : (
                <form onSubmit={(e) => handleJoinWaitlist("annual", e)} className="space-y-3">
                  {!user && (
                    <input
                      type="email"
                      required
                      placeholder={t("pricing.waitlist_email_placeholder", "Enter your email address")}
                      value={annualEmail}
                      onChange={(e) => setAnnualEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-xs focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-white font-medium text-center"
                    />
                  )}
                  {user && (
                    <div className="p-3 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-600 text-center">
                      {user.email}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={waitlistLoading}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-2xl text-xs flex items-center justify-center gap-1.5 shadow-xs"
                  >
                    {waitlistLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                    ) : (
                      <Mail className="w-4 h-4 text-white" />
                    )}
                    <span>{t("pricing.btn_join_waitlist", "Join Waitlist")}</span>
                  </Button>
                </form>
              )}
            </div>
          </Card>

        </div>
      </section>

      {/* Pricing Footer */}
      <footer className="w-full bg-white border-t border-slate-200 py-12 px-4 sm:px-6 lg:px-8 mt-auto select-none">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1.5">
            <div className="flex items-center justify-center md:justify-start gap-2.5">
              <div className="w-6 h-6 rounded-lg bg-primary flex items-center justify-center text-white font-black text-xs shadow-4xs">
                T
              </div>
              <span className="font-black text-slate-900 tracking-tight text-xs">
                TEF Canada Trainer
              </span>
            </div>
            <p className="text-[10px] text-slate-500 font-semibold">Independent practice environment for TEF Canada applicants.</p>
          </div>

          <div className="flex items-center gap-6 text-xs font-bold text-slate-500">
            <Link to="/" className="hover:text-primary transition-colors">{t("common.home", "Home")}</Link>
            <Link to="/pricing" className="hover:text-primary transition-colors">{t("pricing.title", "Pricing")}</Link>
            {!user && <Link to="/auth" className="hover:text-primary transition-colors">{t("settings.login", "Login")}</Link>}
            {user && <Link to="/dashboard" className="hover:text-primary transition-colors">{t("onboarding.go_to_dashboard", "Dashboard")}</Link>}
          </div>

          <p className="text-[10px] text-slate-400 font-semibold max-w-xs leading-normal">
            This platform is not officially associated with or endorsed by CCI Paris Île-de-France or IRCC.
          </p>
        </div>
      </footer>
    </div>
  );
}
